'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { FormationProps } from '@/types/formation';
import FormationCardGlow from '@/components/blocks/formation-card';
import { formationsList } from '@/data/formations';
import { Button } from '@/components/ui/button';

export const dummyFormations: FormationProps[] = formationsList;

const categories = ['Toutes', ...new Set(dummyFormations.map((formation) => formation.category))];
const sortOptions = [
  { value: 'pertinence', label: 'Pertinence' },
  { value: 'alpha', label: 'A-Z' },
  { value: 'duration', label: 'Duree la plus courte' },
];

const extractDurationHours = (duration: string) => {
  const match = duration.match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};

export default function FormationsPageClient() {
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('pertinence');

  const filteredFormations = useMemo(() => {
    let result = [...dummyFormations];

    if (selectedCategory !== 'Toutes') {
      result = result.filter((formation) => formation.category === selectedCategory);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(
        (formation) =>
          formation.title.toLowerCase().includes(term) ||
          formation.description.toLowerCase().includes(term) ||
          formation.category.toLowerCase().includes(term)
      );
    }

    if (sortBy === 'alpha') {
      result.sort((a, b) => a.title.localeCompare(b.title, 'fr'));
    }

    if (sortBy === 'duration') {
      result.sort((a, b) => extractDurationHours(a.duration) - extractDurationHours(b.duration));
    }

    return result;
  }, [searchTerm, selectedCategory, sortBy]);

  return (
    <div className="container mx-auto max-w-7xl px-4 pb-12 pt-10">
      <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#06122F] via-[#050B18] to-[#0A1638] p-6 shadow-[0_20px_80px_-50px_rgba(0,0,0,0.8)] md:p-10">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-accent/15 blur-3xl" />
          <div className="absolute -bottom-28 -right-20 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
        </div>

        <div className="relative grid gap-6 lg:grid-cols-[1.5fr_1fr] lg:items-end">
          <div>
            <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80">
              Catalogue SDPSI
            </span>
            <h1 className="mt-5 text-3xl font-bold tracking-tight text-white md:text-6xl">
              Trouvez la formation securite adaptee a votre equipe
            </h1>
            <p className="mt-5 max-w-3xl text-base text-white/75 md:text-lg">
              Comparez les programmes, filtrez par categorie et demandez un accompagnement pour deployer rapidement vos sessions en entreprise.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-bold text-white">{dummyFormations.length}</p>
              <p className="text-xs text-white/60">Formations</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-bold text-white">Intra</p>
              <p className="text-xs text-white/60">Partout en France</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-bold text-white">48h</p>
              <p className="text-xs text-white/60">Reponse devis</p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild variant="cta">
            <Link href="/contact">Parler a un conseiller formation</Link>
          </Button>
        </div>
      </section>

      <section className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm backdrop-blur md:p-6">
        <div className="grid gap-4 md:grid-cols-[1fr_220px] lg:grid-cols-[1fr_220px_220px]">
          <label className="sr-only" htmlFor="formation-search">
            Rechercher une formation
          </label>
          <input
            id="formation-search"
            type="search"
            placeholder="Rechercher une formation, une categorie, un besoin..."
            className="w-full rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder:text-white/40 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/20"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <label className="sr-only" htmlFor="formation-sort">
            Trier les formations
          </label>
          <select
            id="formation-sort"
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-white focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/20"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <p className="flex items-center text-sm font-medium text-white/70">{filteredFormations.length} resultat(s)</p>
        </div>
      </section>

      <section className="my-6 flex flex-wrap gap-2" aria-label="Filtres par categorie">
        {categories.map((category) => {
          const active = selectedCategory === category;
          return (
            <button
              key={category}
              type="button"
              className={
                active
                  ? 'rounded-full border border-accent/30 bg-accent/15 px-4 py-2 text-sm font-semibold text-white'
                  : 'rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-accent/30 hover:text-white'
              }
              aria-pressed={active}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          );
        })}
      </section>

      {filteredFormations.length > 0 ? (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filteredFormations.map((formation) => (
            <div key={formation.id} className="flex h-full w-full">
              <FormationCardGlow {...formation} />
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-16 text-center">
          <p className="text-lg font-medium text-white">Aucune formation ne correspond a votre recherche.</p>
          <p className="mt-2 text-sm text-white/60">Essayez un autre mot-cle ou reinitialisez vos filtres.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('Toutes');
                setSortBy('pertinence');
              }}
            >
              Reinitialiser les filtres
            </Button>
            <Button asChild variant="cta">
              <Link href="/contact">Demander une recommandation</Link>
            </Button>
          </div>
        </div>
      )}

      <section className="mt-10 rounded-2xl border border-accent/20 bg-white/5 p-6">
        <h2 className="text-xl font-semibold text-white">Besoin d&apos;un parcours sur mesure ?</h2>
        <p className="mt-2 text-white/70">
          Nous composons des parcours multi-formations adaptes a vos contraintes operationnelles et budgetaires.
        </p>
        <div className="mt-4">
          <Button asChild variant="cta">
            <Link href="/contact">Recevoir un plan de formation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
